import { Flex, Text } from "@chakra-ui/react";

/**
 * Trilha (breadcrumb) padrão do sistema — GEN-3497.
 *
 * É o componente COMPARTILHADO: qualquer tela que precise mostrar caminho usa este, para
 * não termos três trilhas com três tamanhos. A barra global é o primeiro consumidor.
 *
 * O desenho segue o **Notion**: a trilha é contexto acima do título, nunca o título. Ela
 * já foi 14px, quando substituía o nome da tela; desde a r6 o nome voltou para o `PageHeader`
 * e a trilha voltou ao tamanho de trilha — o MESMO 12px que a Base Cadastral e o ISS já usam,
 * senão o sistema teria duas trilhas com dois tamanhos. Aqui:
 *
 *   - 12px, Poppins, sem negrito em nenhum degrau;
 *   - degraus anteriores em cinza médio, o atual em cinza escuro — hierarquia por COR,
 *     não por peso;
 *   - separador `/` de baixo contraste, sem virar traço forte;
 *   - degrau clicável ganha fundo cinza claro arredondado no hover (a affordance do
 *     Notion), o atual não ganha nada porque não leva a lugar nenhum.
 */

export interface IBreadcrumbStep {
  label: string;
  /** Ausente = degrau atual, não clicável. */
  onClick?: () => void;
}

interface IBreadcrumbProps {
  steps: IBreadcrumbStep[];
  /** Rótulo do landmark de navegação para leitor de tela. */
  ariaLabel?: string;
}

/** Tokens da trilha, exportados para quem precisar alinhar algo ao lado dela. */
export const breadcrumbTokens = {
  fontSize: "12px",
  /** Degraus anteriores: cinza leve — a trilha é contexto, não conteúdo. */
  ancestor: "#8E9099",
  /** Degrau atual: um passo mais escuro, o suficiente para se destacar sem pesar. */
  current: "#46464A",
  separator: "#C8C6CA",
  /** Cor que o degrau clicável alcança no hover — a única vez que a trilha fica escura. */
  hoverText: "#303033",
} as const;

export const Breadcrumb = ({ steps, ariaLabel = "Caminho" }: IBreadcrumbProps) => (
  <Flex as="nav" aria-label={ariaLabel} align="center" gap="2px" minW="0" flexShrink={1} overflow="hidden">
    {steps.map((step, index) => {
      const isLast = index === steps.length - 1;
      const clickable = Boolean(step.onClick) && !isLast;

      return (
        <Flex key={`${step.label}-${index}`} align="center" gap="2px" minW="0">
          {index > 0 ? (
            <Text
              as="span"
              aria-hidden="true"
              color={breadcrumbTokens.separator}
              fontSize={breadcrumbTokens.fontSize}
              px="2px"
              flexShrink={0}
            >
              /
            </Text>
          ) : null}

          {clickable ? (
            <Text
              as="button"
              type="button"
              fontSize={breadcrumbTokens.fontSize}
              fontWeight="400"
              color={breadcrumbTokens.ancestor}
              px="6px"
              py="2px"
              borderRadius="4px"
              whiteSpace="nowrap"
              // O cinza do hover precisa de contraste REAL contra o fundo da faixa
              // (#F8FAFC): #F2F0F4 fica a 2% dele e o retorno some.
              sx={{
                transition: "background-color 150ms ease, color 150ms ease",
                "&:hover": { backgroundColor: "#E2E8F0", color: breadcrumbTokens.hoverText },
                "&:focus-visible": { boxShadow: "0 0 0 2px #375CA9", outline: "none" },
              }}
              title={step.label}
              onClick={step.onClick}
            >
              {step.label}
            </Text>
          ) : (
            <Text
              fontSize={breadcrumbTokens.fontSize}
              fontWeight="400"
              // Cinza leve, nunca negrito: no Notion a hierarquia da trilha é por cor.
              color={isLast ? breadcrumbTokens.current : breadcrumbTokens.ancestor}
              px="6px"
              py="2px"
              noOfLines={1}
              title={step.label}
              aria-current={isLast ? "page" : undefined}
            >
              {step.label}
            </Text>
          )}
        </Flex>
      );
    })}
  </Flex>
);
